import { useCallback, useState } from 'react'
import { useRegisterSW } from 'virtual:pwa-register/react'

/*
 * Le service worker, vu depuis React.
 *
 * Deux rôles, et un seul point d'entrée pour les deux : enregistrer le worker
 * au démarrage — c'est lui qui rend l'app démarrable hors ligne — et signaler
 * qu'une nouvelle version attend d'être appliquée.
 *
 * Le plugin est configuré en `prompt` (voir vite.config.ts) : la nouvelle
 * version s'installe en arrière-plan mais reste EN ATTENTE tant que personne
 * ne l'active. Ce hook est donc le seul chemin par lequel une mise à jour
 * atteint un utilisateur — s'il n'est monté nulle part, l'app se fige à jamais
 * sur la version installée le premier jour.
 */

/*
 * Une PWA ne cherche une mise à jour qu'au chargement de la page. Ariariko est
 * une app qu'on garde ouverte : sur un téléphone qui ne redémarre jamais
 * l'onglet, ce chargement peut remonter à des semaines. D'où une vérification
 * périodique — une heure, assez rare pour ne rien coûter en données mobiles,
 * ce qui est un vrai sujet ici.
 */
const CHECK_INTERVAL = 60 * 60 * 1000

// Au niveau du module et non dans le hook : en StrictMode le montage est joué
// deux fois, et deux intervalles resteraient à tourner en parallèle.
let checkTimer: ReturnType<typeof setInterval> | undefined

// Gardée pour la vérification MANUELLE (bouton du Profil) : `useRegisterSW` ne
// rend pas l'enregistrement, il ne le passe qu'une fois, à l'inscription.
let currentRegistration: ServiceWorkerRegistration | undefined

function scheduleUpdateChecks(registration: ServiceWorkerRegistration | undefined) {
  if (!registration) return
  currentRegistration = registration
  if (checkTimer !== undefined) clearInterval(checkTimer)
  checkTimer = setInterval(() => {
    // Inutile d'aller frapper au réseau quand il n'y en a pas : `update()`
    // échouerait, et l'app est faite pour vivre des jours sans connexion.
    if (navigator.onLine) void registration.update()
  }, CHECK_INTERVAL)
}

/**
 * Où en est une vérification demandée à la main.
 *
 * ⚠️ Il n'existe AUCUN avancement chiffré à afficher : le service worker ne
 * remonte pas la progression de son précache à la page. Un pourcentage serait
 * inventé — d'où ces états, qui sont réels, et une barre indéterminée.
 */
export type UpdateStatus =
  /** Rien en cours. */
  | 'idle'
  /** On interroge le serveur. */
  | 'checking'
  /** Une version neuve arrive : le worker la met en cache. */
  | 'downloading'
  /** Elle est prête, il ne reste qu'à l'activer. */
  | 'ready'
  /** Vérifié : c'est déjà la dernière version. */
  | 'current'
  /** Pas de réseau : rien à vérifier, et le dire vaut mieux que tourner. */
  | 'offline'

export type AppUpdate = {
  /** Une version est installée et attend d'être activée. */
  updateAvailable: boolean
  /** Active la version en attente. La page se recharge. */
  applyUpdate: () => void
  /** Où en est la vérification demandée à la main. */
  status: UpdateStatus
  /** Va voir s'il existe une version plus récente. */
  checkForUpdate: () => Promise<void>
}

/*
 * Deux consommateurs depuis le 2026-09-06 : la bannière (montée à la racine,
 * elle enregistre le worker) et le Profil (bouton « Vérifier »). Deux appels
 * à `navigator.serviceWorker.register()` sur le MÊME script et le même scope
 * ne créent qu'un seul worker — le navigateur rend l'enregistrement existant.
 * Et l'intervalle de vérification est gardé au niveau du module, donc un seul
 * survit. C'est sans danger : ne pas « corriger » en croyant à un doublon.
 */
export function useAppUpdate(): AppUpdate {
  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({ onRegisteredSW: (_url, registration) => scheduleUpdateChecks(registration) })

  const [checked, setChecked] = useState<UpdateStatus>('idle')

  /*
   * DÉRIVÉ pendant le rendu, jamais recopié dans un état par un effet : une
   * version qui attend l'emporte sur n'importe quel résultat de vérification,
   * et l'y refléter par un `setState` ne ferait qu'ajouter un rendu de plus.
   *
   * ⚠️ `currentRegistration.waiting` est relu ici parce qu'une version peut
   * DÉJÀ attendre, repérée avant que ce composant ne soit monté — la bannière,
   * elle, tourne depuis le démarrage, et son `updatefound` ne se rejouera pas
   * pour nous. Sans cette relecture, le Profil annoncerait « à jour » pendant
   * que la bannière propose la nouvelle version.
   */
  const status: UpdateStatus =
    needRefresh || currentRegistration?.waiting ? 'ready' : checked

  const checkForUpdate = useCallback(async () => {
    if (!navigator.onLine) { setChecked('offline'); return }

    setChecked('checking')
    const registration = currentRegistration
    // Pas de worker enregistré — le cas du serveur de développement, où le
    // service worker n'est pas monté. Rien à chercher.
    if (!registration) { setChecked('current'); return }

    try {
      await registration.update()
    } catch {
      // `update()` ne rejette que sur un problème réseau : le dire plutôt que
      // de laisser croire que l'app est à jour.
      setChecked('offline')
      return
    }

    // `installing` est renseigné à l'instant où une version neuve commence à
    // se mettre en cache — c'est le seul signal réel de « ça télécharge ».
    // `needRefresh` prendra le relais quand elle sera prête (voir `status`).
    if (registration.installing) setChecked('downloading')
    else if (registration.waiting) setChecked('ready')
    else setChecked('current')
  }, [])

  return {
    updateAvailable: needRefresh,
    // `true` : on active la version en attente ET on recharge. Sans le
    // rechargement, l'onglet continuerait de tourner sur l'ancien code face à
    // un worker neuf — exactement le mélange de versions qu'on cherche à
    // éviter en n'utilisant pas `autoUpdate`.
    applyUpdate: () => { void updateServiceWorker(true) },
    status,
    checkForUpdate,
  }
}
